'use client';

import classNames from 'classnames';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectOpenModals } from './redux/modals.selectors';
import { ModalProps } from './types';

interface ModalContainerProps extends ModalProps {
  modalName: string;
  isSearchEmpty?: boolean;
  searchInputRef?: RefObject<HTMLInputElement | null>;
  isModalOpen?: boolean;
  onClose: () => void;
  shouldCloseOnEscape?: boolean;
  shouldCloseOnBackgroundClick?: boolean;
  boxClassName?: string;
}

const ModalContainer = ({
  className,
  variant,
  modalName,
  isSearchEmpty,
  searchInputRef,
  isModalOpen,
  children,
  onClose,
  shouldCloseOnEscape,
  shouldCloseOnBackgroundClick = false,
  boxClassName,
  ...rest
}: ModalContainerProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const openModals = useSelector(selectOpenModals);
  const isModalActive = useMemo(
    () =>
      openModals?.find((m) => m.modalName === modalName)?.modalOrder ===
      openModals?.length,
    [modalName, openModals]
  );

  const setFocus = useCallback(() => {
    modalRef?.current?.focus();
    searchInputRef?.current?.focus();
  }, [searchInputRef]);

  useEffect(() => {
    if (isModalActive) {
      setFocus();
    }
  }, [isModalActive, setFocus]);

  useEffect(() => {
    if (isModalOpen) {
      setFocus();
    }
  }, [isModalOpen, setFocus]);

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking on the background (not the modal content)
    if (shouldCloseOnBackgroundClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      shouldCloseOnEscape &&
      e.key === 'Escape' &&
      (!!isSearchEmpty || typeof isSearchEmpty === 'undefined') &&
      isModalActive
    ) {
      onClose();
    }
  };

  return (
    <div
      className={classNames(
        'modal z-40',
        {
          'modal-light-bg': variant === 'narrow',
        },
        className
      )}
      onClick={handleBackgroundClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      tabIndex={0}
      {...rest}>
      <div
        className={classNames(
          'modal-box outline-base-100/[.16] flex max-h-full flex-col p-0 outline outline-8',
          {
            'absolute top-4 right-4 bottom-4 w-2/3 max-w-full 2xl:w-1/2':
              variant === 'default',
            'absolute top-4 right-4 bottom-4 w-[600px] min-w-[600px]':
              variant === 'narrow',
            // Centered variant: let DaisyUI's `.modal` grid place-items-center
            // center the box naturally; caller sets size via boxClassName.
            '!rounded-3xl': variant === 'centered',
          },
          boxClassName
        )}>
        {children}
      </div>
    </div>
  );
};

export default ModalContainer;
